'use client';

import React, { createContext, useContext, useEffect, useState, useCallback, useRef } from 'react';
import { createClient } from '@/lib/supabase/client';
// Eagerly import WagmiInnerConsumer to avoid dynamic import delay on wallet connect
import WagmiInnerConsumerDirect from './WagmiInnerConsumer';

// ─── Super Admin Wallet ───────────────────────────────────────────────────────
export const ADMIN_WALLET_ADDRESS = '0x9562f19ed1ff4074b1f6acc02109eaa482176186'; // lowercase for comparison
// ─────────────────────────────────────────────────────────────────────────────

interface WalletUser {
  id: string;
  walletAddress: string;
  email: string;
  fullName: string;
  role: string;
  referralCode: string;
  onboardingCompleted: boolean;
  isBanned: boolean;
}

interface WalletAuthContextType {
  walletUser: WalletUser | null;
  loading: boolean;
  initializing: boolean;
  isConnected: boolean;
  walletAddress: string | undefined;
  connectWallet: () => void;
  disconnectWallet: () => void;
  signOut: () => Promise<void>;
  refreshUser: () => Promise<void>;
  supabaseUser: any;
  supabaseSession: any;
  isAdminWallet: boolean;
}

const defaultContext: WalletAuthContextType = {
  walletUser: null,
  loading: false,
  initializing: true,
  isConnected: false,
  walletAddress: undefined,
  connectWallet: () => {},
  disconnectWallet: () => {},
  signOut: async () => {},
  refreshUser: async () => {},
  supabaseUser: null,
  supabaseSession: null,
  isAdminWallet: false,
};

const WalletAuthContext = createContext<WalletAuthContextType>(defaultContext);

export const useWalletAuth = () => useContext(WalletAuthContext);

// Keep backward compat with existing useAuth calls
export const useAuth = () => {
  const ctx = useWalletAuth();
  return {
    user: ctx.supabaseUser,
    session: ctx.supabaseSession,
    loading: ctx.loading,
    signOut: ctx.signOut,
    walletUser: ctx.walletUser,
    isConnected: ctx.isConnected,
    walletAddress: ctx.walletAddress,
    isAdminWallet: ctx.isAdminWallet,
  };
};

interface WalletAuthProviderProps {
  children: React.ReactNode;
  wagmiReady?: boolean;
}

const INACTIVITY_TIMEOUT_MS = 20 * 60 * 1000; // 20 minutes

export function WalletAuthProvider({ children, wagmiReady = false }: WalletAuthProviderProps) {
  const [walletUser, setWalletUser] = useState<WalletUser | null>(null);
  const [loading, setLoading] = useState(false);
  const [initializing, setInitializing] = useState(true);
  const [supabaseUser, setSupabaseUser] = useState<any>(null);
  const [supabaseSession, setSupabaseSession] = useState<any>(null);
  const [address, setAddress] = useState<string | undefined>(undefined);
  const [isConnected, setIsConnected] = useState(false);
  const [wagmiActions, setWagmiActions] = useState<{
    connect: ((args: any) => void) | null;
    connectors: any[];
    disconnect: (() => void) | null;
  }>({ connect: null, connectors: [], disconnect: null });

  const inactivityTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const supabase = createClient();

  // Derive isAdminWallet from connected address
  const isAdminWallet = isConnected && !!address && address.toLowerCase() === ADMIN_WALLET_ADDRESS;

  // Track Supabase session
  useEffect(() => {
    supabase.auth.getSession().then(({ data: { session } }) => {
      setSupabaseSession(session);
      setSupabaseUser(session?.user ?? null);
    });
    const { data: { subscription } } = supabase.auth.onAuthStateChange((_event, session) => {
      setSupabaseSession(session);
      setSupabaseUser(session?.user ?? null);
    });
    return () => subscription.unsubscribe();
  }, []);

  // Mark initializing as false once wagmi has reported its connection state
  const wagmiReportedRef = React.useRef(false);
  const handleConnected = React.useCallback((connected: boolean, settled: boolean = true) => {
    setIsConnected(connected);
    // Dev/QA: when the mock wallet is enabled, keep initializing until the mock actually
    // connects, so the auto-connect effect can win the race against route guards on reload.
    const mockEnabled = process.env.NEXT_PUBLIC_ENABLE_MOCK_WALLET === 'true';
    if (mockEnabled && !connected) return;
    // Only finish initializing once wagmi has settled (not mid-reconnect). This prevents a
    // brief "disconnected" flash during reconnect from bouncing logged-in users to login.
    if (!wagmiReportedRef.current && settled) {
      wagmiReportedRef.current = true;
      setInitializing(false);
    }
  }, []);

  // If wagmiReady never becomes true (no wagmi), stop initializing after a short delay.
  // Dev/QA: when the mock wallet is enabled, give wagmi + mock auto-connect time to report
  // before declaring "not connected" (otherwise guards bounce us to /sign-up-login on reload).
  useEffect(() => {
    if (!wagmiReady) {
      const delay = process.env.NEXT_PUBLIC_ENABLE_MOCK_WALLET === 'true' ? 4000 : 100;
      const t = setTimeout(() => setInitializing(false), delay);
      return () => clearTimeout(t);
    }
  }, [wagmiReady]);

  const performSignOut = useCallback(async () => {
    // Store current IP so returning same-IP visitor skips wallet prompt
    try {
      const res = await fetch('https://api.ipify.org?format=json', { cache: 'no-store' });
      const data = await res.json();
      if (data?.ip) localStorage.setItem('last_logout_ip', data.ip);
    } catch {}
    // Disconnect wagmi wallet
    if (wagmiActions.disconnect) {
      try { wagmiActions.disconnect(); } catch {}
    }
    // Clear all state
    setWalletUser(null);
    setIsConnected(false);
    setAddress(undefined);
    // Sign out from supabase
    try { await supabase.auth.signOut(); } catch {}
    // Clear inactivity timer
    if (inactivityTimerRef.current) {
      clearTimeout(inactivityTimerRef.current);
      inactivityTimerRef.current = null;
    }
  }, [wagmiActions]);

  // Inactivity timer — reset on any user interaction
  const resetInactivityTimer = useCallback(() => {
    if (inactivityTimerRef.current) {
      clearTimeout(inactivityTimerRef.current);
      inactivityTimerRef.current = null;
    }
    // Only set timer if user is logged in (wallet or supabase session)
    if (isConnected || supabaseUser || walletUser) {
      inactivityTimerRef.current = setTimeout(() => {
        performSignOut();
      }, INACTIVITY_TIMEOUT_MS);
    }
  }, [isConnected, supabaseUser, walletUser, performSignOut]);

  // Attach activity listeners
  useEffect(() => {
    const events = ['mousemove', 'mousedown', 'keydown', 'touchstart', 'scroll', 'click'];
    const handleActivity = () => resetInactivityTimer();

    events.forEach(e => window.addEventListener(e, handleActivity, { passive: true }));
    resetInactivityTimer(); // start timer on mount/state change

    return () => {
      events.forEach(e => window.removeEventListener(e, handleActivity));
      if (inactivityTimerRef.current) clearTimeout(inactivityTimerRef.current);
    };
  }, [resetInactivityTimer]);

  // Mints (or refreshes) a Supabase session for the given wallet via the server, then applies
  // it to the browser client. Idempotent and safe to call on every wallet load; replaces any
  // stale session that may belong to a different wallet. Silent on failure (e.g. not onboarded).
  const mintWalletSession = useCallback(async (normalizedAddr: string) => {
    try {
      const res = await fetch('/api/wallet-session', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ walletAddress: normalizedAddr }),
      });
      if (!res.ok) return; // 404 = not onboarded yet; other errors leave existing session intact
      const tokens = await res.json();
      if (tokens?.access_token && tokens?.refresh_token) {
        await supabase.auth.setSession({
          access_token: tokens.access_token,
          refresh_token: tokens.refresh_token,
        });
      }
    } catch {
      /* session bootstrap failed — reads may be limited until the next attempt */
    }
  }, [supabase]);

  const loadWalletUser = useCallback(async (addr: string) => {
    try {
      setLoading(true);
      const normalizedAddr = addr.toLowerCase();

      // Admin wallet gets instant UI access — no DB lookup required for the grant.
      if (normalizedAddr === ADMIN_WALLET_ADDRESS) {
        setWalletUser({
          id: 'admin-wallet',
          walletAddress: normalizedAddr,
          email: 'admin@aidaram.net',
          fullName: 'Super Admin',
          role: 'admin',
          referralCode: '',
          onboardingCompleted: true,
          isBanned: false,
        });
        // Always (re)establish a real Supabase admin session that matches THIS wallet, so
        // RLS-protected admin reads (event_logs visitors, user lists, etc.) work client-side.
        // We intentionally don't trust a pre-existing session: it may belong to a different
        // (non-admin) wallet still cached in cookies/storage.
        await mintWalletSession(normalizedAddr);
        setLoading(false);
        return;
      }

      // Establish a real Supabase session that matches THIS wallet so RLS-protected reads work.
      // (Wallet users otherwise have no auth.uid(), which blocks every client-side read.)
      // Always (re)mint so a stale session from a previously-connected wallet can't leak through.
      // 404 (not_onboarded) is expected for brand-new wallets — they'll onboard first.
      await mintWalletSession(normalizedAddr);

      const { data: profile } = await supabase
        .from('user_profiles')
        .select('*')
        .eq('wallet_primary', normalizedAddr)
        .single();

      if (profile && !profile.is_banned) {
        setWalletUser({
          id: profile.id,
          walletAddress: normalizedAddr,
          email: profile.email || '',
          fullName: profile.full_name || '',
          role: profile.role || 'user',
          referralCode: profile.referral_code || '',
          onboardingCompleted: profile.onboarding_completed || false,
          isBanned: profile.is_banned || false,
        });
        // Fire-and-forget last_seen_at update — don't block the auth flow
        supabase
          .from('user_profiles')
          .update({ last_seen_at: new Date().toISOString() })
          .eq('id', profile.id)
          .then(() => {}, () => {});
      } else {
        // Wallet not registered yet — don't set walletUser
        setWalletUser(null);
      }
    } catch (err) {
      console.error('loadWalletUser error:', err);
      setWalletUser(null);
    } finally {
      setLoading(false);
    }
  }, [supabase, mintWalletSession]);

  useEffect(() => {
    if (isConnected && address) {
      loadWalletUser(address);
    } else if (!isConnected) {
      setWalletUser(null);
    }
  }, [isConnected, address, loadWalletUser]);

  const connectWallet = useCallback(() => {
    if (wagmiActions.connect && wagmiActions.connectors.length > 0) {
      // Dev/QA: if a mock connector is present, prefer it (no real wallet in headless browser)
      const mock = wagmiActions.connectors.find((c: any) => c.id === 'mock' || c.type === 'mock');
      // Try injected (MetaMask) first, fallback to first available connector
      const injected = wagmiActions.connectors.find((c: any) => c.id === 'injected' || c.type === 'injected');
      const connector = mock || injected || wagmiActions.connectors[0];
      if (connector) {
        wagmiActions.connect({ connector });
      }
    }
  }, [wagmiActions]);

  // Dev/QA only: auto-reconnect the mock wallet across full page reloads so flows that use
  // window.location navigation (e.g. "Go to Dashboard") keep the test wallet connected.
  const mockAutoConnectedRef = useRef(false);
  useEffect(() => {
    if (process.env.NEXT_PUBLIC_ENABLE_MOCK_WALLET !== 'true') return;
    if (mockAutoConnectedRef.current || isConnected) return;
    const mock = wagmiActions.connectors.find((c: any) => c.id === 'mock' || c.type === 'mock');
    if (mock && wagmiActions.connect) {
      mockAutoConnectedRef.current = true;
      wagmiActions.connect({ connector: mock });
    }
  }, [wagmiActions, isConnected]);

  const disconnectWallet = useCallback(() => {
    if (wagmiActions.disconnect) wagmiActions.disconnect();
    setWalletUser(null);
    setIsConnected(false);
    setAddress(undefined);
  }, [wagmiActions]);

  const signOut = useCallback(async () => {
    await performSignOut();
  }, [performSignOut]);

  const refreshUser = useCallback(async () => {
    if (address) await loadWalletUser(address);
  }, [address, loadWalletUser]);

  return (
    <WalletAuthContext.Provider value={{
      walletUser,
      loading,
      initializing,
      isConnected,
      walletAddress: address,
      connectWallet,
      disconnectWallet,
      signOut,
      refreshUser,
      supabaseUser,
      supabaseSession,
      isAdminWallet,
    }}>
      {wagmiReady && (
        <WagmiHooksConsumer
          onAddress={setAddress}
          onConnected={handleConnected}
          onActions={setWagmiActions}
        />
      )}
      {children}
    </WalletAuthContext.Provider>
  );
}

// This component only renders when wagmiReady=true (inside WagmiProvider)
function WagmiHooksConsumer({ onAddress, onConnected, onActions }: {
  onAddress: (addr: string | undefined) => void;
  onConnected: (connected: boolean, settled: boolean) => void;
  onActions: (actions: { connect: any; connectors: any[]; disconnect: any }) => void;
}) {
  // Use eagerly imported component — no dynamic import delay
  return <WagmiInnerConsumerDirect onAddress={onAddress} onConnected={onConnected} onActions={onActions} />;
}
